import React, { useState, useEffect } from 'react';
import axios from "axios";
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import PhonelinkSetupOutlined from '@mui/icons-material/PhonelinkSetupOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import { DataTable, EmptyState, PageHeader, type DataTableColumn } from '@routes-plan/ui';
import { Office } from '../../../models/office';
import { Route } from '../../../models/route';
import useAxiosInterceptor from '../../AxiosInterceptor';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useT, type TextKey } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';
import OfficeRouteSelects from '../components/OfficeRouteSelects';
import { filterGridSx, formActionsSx, pageSx, sectionSx } from '../components/layout';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const RouteDevices: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
  useAxiosInterceptor();
  const t = useT();
  setPageTitle('routeDevices.title');
  const [selectedRoutes, setSelectedRoutes] = useState<number[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const timeMinutes = ['00', '15', '30', '45'] as const;
  const timeFullOptions = Array.from({ length: 24 }, (_, i) =>
    timeMinutes.map((m) => `${String(i).padStart(2, '0')}:${m}`)
  ).flat();

  type DeviceSettings = {
    allowMultipleLoans: boolean;
    allowClientCreditLimit: boolean;
    allowManualDiscount: boolean;
    allowRepay: boolean;
    allowRefinance: boolean;
    mobileSessionEnabled: boolean;
    deviceDetail: boolean;
    enforceSchedule: boolean;
    openingHour: string;
    openingMinute: string;
    closingHour: string;
    closingMinute: string;
    allowDeletePayment: boolean;
    allowDeleteLoan: boolean;
    allowDeviceRegistration: boolean;
    manualInsurance: boolean;
  };

  const [deviceSettingsByRoute, setDeviceSettingsByRoute] = useState<Record<number, DeviceSettings>>({});

  const [office_id, setOffice_id] = useState('');
  const [officeRoutes, setOfficeRoutes] = useState<Route[]>([]);
  const { canUpdate } = useResourcePermissions('/loans/route-devices');

  const resetConfigurationState = () => {
    setSelectedRoutes([]);
    setOfficeRoutes([]);
    setDeviceSettingsByRoute({});
  };

  const parseTimeToParts = (
    value: string | undefined | null,
    fallbackHour: string,
    fallbackMinute: string
  ) => {
    const safeValue = (value ?? '').trim();
    if (!safeValue) {
      return {
        hour: fallbackHour.padStart(2, '0'),
        minute: fallbackMinute.padStart(2, '0'),
      };
    }

    const [hour = fallbackHour, minute = fallbackMinute] = safeValue.split(':');
    return {
      hour: (hour || fallbackHour).trim().padStart(2, '0'),
      minute: (minute || fallbackMinute).trim().padStart(2, '0'),
    };
  };

  const defaultDeviceSettings: DeviceSettings = {
    allowMultipleLoans: false,
    allowClientCreditLimit: false,
    allowManualDiscount: true,
    allowRepay: false,
    allowRefinance: false,
    mobileSessionEnabled: true,
    deviceDetail: false,
    enforceSchedule: false,
    openingHour: '08',
    openingMinute: '00',
    closingHour: '20',
    closingMinute: '00',
    allowDeletePayment: false,
    allowDeleteLoan: false,
    allowDeviceRegistration: false,
    manualInsurance: false,
  };

  useEffect(() => {
    (async () => {
      try {
        showLoading();
        const { data } = await axios.get('offices');
        setOffices(data.data);
        await new Promise((resolve) => setTimeout(resolve, 2000));
        showAlert(t('routeDevices.queryOk'), 'info');
      } catch (error) {
        showAlert(t('common.loadError'), 'error');
      } finally {
        hideLoading();
      }
    })();
  }, []);

  useEffect(() => {
    resetConfigurationState();
  }, []);

  useEffect(() => {
    if (office_id) {
      resetConfigurationState();

      (async () => {
        showLoading();
        try {
          const { data } = await axios.get(`offices/${office_id}/route-devices`);
          setOfficeRoutes(data);
          const deviceMap: Record<number, DeviceSettings> = {};

          data.forEach((route: Route) => {
            const routeId = route.id as number;

            const openParts = parseTimeToParts(route.openingTime, '08', '00');
            const closeParts = parseTimeToParts(route.closingTime, '20', '00');

            deviceMap[routeId] = {
              allowMultipleLoans: Boolean(route.allowMultipleLoans),
              allowClientCreditLimit: Boolean(Number(route.allowClientCreditLimit ?? 0)),
              allowManualDiscount: Boolean(Number(route.allowManualDiscount ?? 1)),
              allowRepay: Boolean(route.allowRepay),
              allowRefinance: Boolean(route.allowRefinance),
              mobileSessionEnabled:
                Number(route.user?.mobileSessionEnabled ?? 1) === 1,
              deviceDetail: Boolean(route.deviceDetail),
              enforceSchedule: Boolean(route.enforceSchedule),
              openingHour: openParts.hour,
              openingMinute: openParts.minute,
              closingHour: closeParts.hour,
              closingMinute: closeParts.minute,
              allowDeletePayment: Boolean(route.allowDeletePayment),
              allowDeleteLoan: Boolean(route.allowDeleteLoan),
              allowDeviceRegistration: Boolean(route.allowDeviceRegistration),
              manualInsurance: Boolean(route.manualInsurance),
            };
          });

          setDeviceSettingsByRoute(deviceMap);

          const selectedIds = data
            .filter((route: Route) => {
              const s = deviceMap[route.id as number];
              return (
                s.allowMultipleLoans ||
                s.allowClientCreditLimit ||
                s.allowManualDiscount ||
                s.allowRepay ||
                s.allowRefinance ||
                s.mobileSessionEnabled ||
                s.deviceDetail ||
                s.enforceSchedule ||
                s.allowDeletePayment ||
                s.allowDeleteLoan ||
                s.allowDeviceRegistration ||
                s.manualInsurance
              );
            })
            .map((route: Route) => Number(route.id))
            .filter((id: number) => !Number.isNaN(id));

          setSelectedRoutes(selectedIds);

          await new Promise((resolve) => setTimeout(resolve, 2000));
          if (data.length > 0) {
            showAlert(t('routeDevices.queryOk'), 'info');
          } else {
            showAlert(t('routeDevices.noRecords'), 'warning');
          }
        } catch (error) {
          showAlert(t('common.loadError'), 'error');
        } finally {
          hideLoading();
        }
      })();
      return;
    }

    resetConfigurationState();
  }, [office_id]);
  const handleCheckboxChange = (routeId: number, isActive: boolean) => {
    if (isActive) {
      setSelectedRoutes((prevSelected: number[]) => {
        return prevSelected.includes(routeId) ? prevSelected : [...prevSelected, routeId];
      });

      // Al marcar "Configurar" en la ruta, se activan todas las casillas de esa fila.
      setDeviceSettingsByRoute((prev) => ({
        ...prev,
        [routeId]: {
          ...(prev[routeId] ?? defaultDeviceSettings),
          allowMultipleLoans: true,
          allowRepay: true,
          allowRefinance: true,
          mobileSessionEnabled: true,
          deviceDetail: true,
          enforceSchedule: true,
          allowDeletePayment: true,
          allowDeleteLoan: true,
          allowDeviceRegistration: true,
          manualInsurance: true,
        },
      }));
    } else {
      setSelectedRoutes((prevSelected: number[]) => prevSelected.filter(id => id !== routeId));

      // Al desmarcar "Configurar" en la ruta, se desactivan todas las casillas de esa fila.
      setDeviceSettingsByRoute((prev) => ({
        ...prev,
        [routeId]: {
          ...(prev[routeId] ?? defaultDeviceSettings),
          allowMultipleLoans: false,
          allowClientCreditLimit: false,
          allowManualDiscount: false,
          allowRepay: false,
          allowRefinance: false,
          mobileSessionEnabled: false,
          deviceDetail: false,
          enforceSchedule: false,
          allowDeletePayment: false,
          allowDeleteLoan: false,
          allowDeviceRegistration: false,
          manualInsurance: false,
        },
      }));
    }
  };

  const handleSelectAll = (isActive: boolean) => {
    if (isActive) {
      const allRouteIds = officeRoutes
        .map((route) => Number(route.id))
        .filter((id) => !Number.isNaN(id));
      setSelectedRoutes(allRouteIds);

      setDeviceSettingsByRoute((prev) => {
        const next = { ...prev };
        allRouteIds.forEach((routeId) => {
          const current = prev[routeId] ?? defaultDeviceSettings;
          next[routeId] = {
            ...current,
            allowMultipleLoans: true,
            allowRepay: true,
            allowRefinance: true,
            mobileSessionEnabled: true,
            deviceDetail: true,
            enforceSchedule: true,
            allowDeletePayment: true,
            allowDeleteLoan: true,
            allowDeviceRegistration: true,
            manualInsurance: true,
          };
        });
        return next;
      });
    } else {
      setSelectedRoutes([]);

      // Si se desactiva el checkall del header, se desactivan TODOS los checks de columnas para todas las filas.
      const allRouteIds = officeRoutes
        .map((route) => Number(route.id))
        .filter((id) => !Number.isNaN(id));

      setDeviceSettingsByRoute((prev) => {
        const next = { ...prev };
        allRouteIds.forEach((routeId) => {
          const current = prev[routeId] ?? defaultDeviceSettings;
          next[routeId] = {
            ...current,
            allowMultipleLoans: false,
            allowClientCreditLimit: false,
            allowManualDiscount: false,
            allowRepay: false,
            allowRefinance: false,
            mobileSessionEnabled: false,
            deviceDetail: false,
            enforceSchedule: false,
            allowDeletePayment: false,
            allowDeleteLoan: false,
            allowDeviceRegistration: false,
            manualInsurance: false,
          };
        });
        return next;
      });
    }
  };

  const handleSave = async () => {
    try {
      if (selectedRoutes.length === 0) {
        showAlert(t('routeDevices.noRoutesSelected'), 'warning');
        return;
      }
      showLoading();
      const payload = selectedRoutes.map((routeId) => {
        const s = deviceSettingsByRoute[routeId];
        return {
          routeId: routeId,
          allowMultipleLoans: s?.allowMultipleLoans ?? false,
          allowClientCreditLimit: s?.allowClientCreditLimit ?? false,
          allowManualDiscount: s?.allowManualDiscount ?? true,
          allowRepay: s?.allowRepay ?? false,
          allowRefinance: s?.allowRefinance ?? false,
          mobileSessionEnabled: s?.mobileSessionEnabled ?? true,
          deviceDetail: s?.deviceDetail ?? false,
          enforceSchedule: s?.enforceSchedule ?? false,
          openingTime: s?.enforceSchedule
            ? `${s?.openingHour ?? '08'}:${s?.openingMinute ?? '00'}`
            : null,
          closingTime: s?.enforceSchedule
            ? `${s?.closingHour ?? '20'}:${s?.closingMinute ?? '00'}`
            : null,
          allowDeletePayment: s?.allowDeletePayment ?? false,
          allowDeleteLoan: s?.allowDeleteLoan ?? false,
          allowDeviceRegistration: s?.allowDeviceRegistration ?? false,
          manualInsurance: s?.manualInsurance ?? false,
        };
      });

      const url = "/route-devices";

      const response = await axios.put(url, payload);

      await new Promise(resolve => setTimeout(resolve, 2000)); // Esperar 2 segundos

      showAlert(t('common.savedOk'), 'success');
    } catch (error) {
      console.error('Error al guardar los datos:', error);
      showAlert(t('common.saveError'), 'error');
    } finally {
      hideLoading();
    }
  };

  const emptyDeviceSettings: DeviceSettings = {
    allowMultipleLoans: false,
    allowClientCreditLimit: false,
    allowManualDiscount: true,
    allowRepay: false,
    allowRefinance: false,
    mobileSessionEnabled: true,
    deviceDetail: false,
    enforceSchedule: false,
    openingHour: '08',
    openingMinute: '00',
    closingHour: '20',
    closingMinute: '00',
    allowDeletePayment: false,
    allowDeleteLoan: false,
    allowDeviceRegistration: false,
    manualInsurance: false,
  };

  type DeviceBooleanKey = Exclude<
    keyof DeviceSettings,
    'openingHour' | 'openingMinute' | 'closingHour' | 'closingMinute'
  >;

  const setRouteDeviceSetting = (
    routeId: number,
    key: DeviceBooleanKey,
    value: boolean
  ) => {
    setDeviceSettingsByRoute((prev) => {
      const current = prev[routeId] ?? emptyDeviceSettings;
      return {
        ...prev,
        [routeId]: {
          ...current,
          [key]: value,
        },
      };
    });

    // Si se cambia alguna casilla de una ruta, esa ruta se incluye en lo que se guarda.
    setSelectedRoutes((prev) => (prev.includes(routeId) ? prev : [...prev, routeId]));
  };

  const setRouteTimeSetting = (
    routeId: number,
    key: 'openingHour' | 'openingMinute' | 'closingHour' | 'closingMinute',
    value: string
  ) => {
    setDeviceSettingsByRoute((prev) => {
      const current = prev[routeId] ?? defaultDeviceSettings;
      return {
        ...prev,
        [routeId]: {
          ...current,
          [key]: value,
        },
      };
    });
  };

  const headerWithTooltip = (label: string, tooltip: string) => (
    <Tooltip title={tooltip} arrow placement="top">
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}>
        {label}
        <InfoOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
      </Box>
    </Tooltip>
  );

  const rows = officeRoutes.map((route: Route) => ({
    id: Number(route.id),
    admin: route.user
      ? `${route.user.firstName ?? ''} ${route.user.lastName ?? ''}`.trim() || route.user.username || t('routeDevices.unassigned')
      : t('routeDevices.unassigned'),
    routeName: route.name,
  }));
  type DeviceRow = (typeof rows)[number];

  /** Permisos de la app por ruta: una columna de casillas por cada uno. */
  const toggles: { key: DeviceBooleanKey; label: TextKey; tooltip: TextKey }[] = [
    { key: 'allowMultipleLoans', label: 'routeDevices.loans', tooltip: 'routeDevices.loansTooltip' },
    { key: 'allowClientCreditLimit', label: 'routeDevices.clientLimit', tooltip: 'routeDevices.clientLimitTooltip' },
    { key: 'allowManualDiscount', label: 'routeDevices.manualDiscount', tooltip: 'routeDevices.manualDiscountTooltip' },
    { key: 'allowRepay', label: 'routeDevices.settle', tooltip: 'routeDevices.settleTooltip' },
    { key: 'allowRefinance', label: 'routeDevices.refinance', tooltip: 'routeDevices.refinanceTooltip' },
    { key: 'mobileSessionEnabled', label: 'routeDevices.session', tooltip: 'routeDevices.sessionTooltip' },
    { key: 'deviceDetail', label: 'routeDevices.detail', tooltip: 'routeDevices.detailTooltip' },
    { key: 'allowDeletePayment', label: 'routeDevices.deletePayments', tooltip: 'routeDevices.deletePaymentsTooltip' },
    { key: 'allowDeleteLoan', label: 'routeDevices.deleteLoans', tooltip: 'routeDevices.deleteLoansTooltip' },
    { key: 'allowDeviceRegistration', label: 'routeDevices.registration', tooltip: 'routeDevices.registrationTooltip' },
    { key: 'manualInsurance', label: 'routeDevices.manualFee', tooltip: 'routeDevices.manualFeeTooltip' },
    { key: 'enforceSchedule', label: 'routeDevices.schedule', tooltip: 'routeDevices.scheduleTooltip' },
  ];

  const timeColumn = (
    id: 'openingTime' | 'closingTime',
    label: TextKey,
    tooltip: TextKey,
    hourKey: 'openingHour' | 'closingHour',
    minuteKey: 'openingMinute' | 'closingMinute',
    fallback: string,
  ): DataTableColumn<DeviceRow> => ({
    id,
    header: headerWithTooltip(t(label), t(tooltip)),
    csvHeader: t(label),
    align: 'center',
    value: (row) => {
      const s = deviceSettingsByRoute[row.id];
      return `${s?.[hourKey] ?? fallback}:${s?.[minuteKey] ?? '00'}`;
    },
    render: (row) => {
      const s = deviceSettingsByRoute[row.id];
      return (
        <TextField
          select
          size="small"
          sx={{ minWidth: 96 }}
          inputProps={{ 'aria-label': `${t(label)} ${row.routeName}` }}
          value={`${s?.[hourKey] ?? fallback}:${s?.[minuteKey] ?? '00'}`}
          onChange={(event) => {
            const [hh, mm] = String(event.target.value).split(':');
            setRouteTimeSetting(row.id, hourKey, hh);
            setRouteTimeSetting(row.id, minuteKey, mm);
          }}
          disabled={!canUpdate || !s?.enforceSchedule}
        >
          {timeFullOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      );
    },
  });

  const allSelected = officeRoutes.length > 0 && selectedRoutes.length === officeRoutes.length;
  const someSelected = selectedRoutes.length > 0 && selectedRoutes.length < officeRoutes.length;

  const columns: DataTableColumn<DeviceRow>[] = [
    {
      id: 'routeName',
      header: headerWithTooltip(t('common.route'), t('routeDevices.routeTooltip')),
      csvHeader: t('common.route'),
      value: (row) => row.routeName,
    },
    {
      id: 'select',
      header: (
        <Tooltip title={t('routeDevices.selectAllTooltip')} arrow placement="top">
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <Checkbox
              size="small"
              indeterminate={someSelected}
              checked={allSelected}
              disabled={!canUpdate}
              onChange={(event) => handleSelectAll(event.target.checked)}
              inputProps={{ 'aria-label': t('routeDevices.selectAllTooltip') }}
            />
            {t('routeDevices.configure')}
          </Box>
        </Tooltip>
      ),
      csv: false,
      align: 'center',
      render: (row) => (
        <Checkbox
          size="small"
          checked={selectedRoutes.includes(row.id)}
          disabled={!canUpdate}
          onChange={(event) => handleCheckboxChange(row.id, event.target.checked)}
          inputProps={{ 'aria-label': `${t('routeDevices.configure')} ${row.routeName}` }}
        />
      ),
    },
    ...toggles.map<DataTableColumn<DeviceRow>>((toggle) => ({
      id: toggle.key,
      header: headerWithTooltip(t(toggle.label), t(toggle.tooltip)),
      csvHeader: t(toggle.label),
      align: 'center',
      value: (row) => Boolean(deviceSettingsByRoute[row.id]?.[toggle.key]),
      csv: (row) => (deviceSettingsByRoute[row.id]?.[toggle.key] ? t('common.yes') : t('common.no')),
      render: (row) => (
        <Checkbox
          size="small"
          checked={Boolean(deviceSettingsByRoute[row.id]?.[toggle.key])}
          disabled={!canUpdate}
          onChange={(event) => setRouteDeviceSetting(row.id, toggle.key, event.target.checked)}
          inputProps={{ 'aria-label': `${t(toggle.label)} ${row.routeName}` }}
        />
      ),
    })),
    timeColumn('openingTime', 'routeDevices.openTime', 'routeDevices.openTimeTooltip', 'openingHour', 'openingMinute', '08'),
    timeColumn('closingTime', 'routeDevices.closeTime', 'routeDevices.closeTimeTooltip', 'closingHour', 'closingMinute', '20'),
  ];

  const saveButton = (
    <PermissionButton
      allowed={canUpdate}
      deniedTooltip={t('routeDevices.noPermissionSave')}
      variant="contained"
      startIcon={<SaveOutlined />}
      onClick={handleSave}
    >
      {t('routeDevices.saveChanges')}
    </PermissionButton>
  );

  return (
    <Box sx={pageSx}>
      <PageHeader title={t('routeDevices.title')} description={t('routeDevices.description')} actions={saveButton} />
      <Paper variant="outlined" sx={sectionSx}>
        <Box sx={filterGridSx}>
          <OfficeRouteSelects offices={offices} officeId={office_id} onOfficeChange={setOffice_id} required />
          <Typography variant="body2" color="text.secondary">
            {selectedRoutes.length > 0 ? t('routeDevices.selectedCount', { count: selectedRoutes.length }) : t('routeDevices.selectHint')}
          </Typography>
        </Box>
      </Paper>
      <DataTable<DeviceRow>
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        searchable
        exportFileName={t('routeDevices.exportFile')}
        ariaLabel={t('routeDevices.title')}
        initialPageSize={50}
        emptyState={
          <EmptyState
            dense
            icon={<PhonelinkSetupOutlined />}
            title={office_id ? t('routeDevices.noRecords') : t('routeDevices.selectOfficeTitle')}
          />
        }
      />
      {officeRoutes.length > 0 ? <Box sx={formActionsSx}>{saveButton}</Box> : null}
    </Box>
  );
};

export default RouteDevices;
